/**
 * ExportOrchestrator — Scans a MenuProfile and classifies option groups
 * as SHARED (linked to >1 item) or UNIQUE (linked to 1 item only).
 *
 * Returns structured data ready for template sheet builders.
 */

import type { MenuProfile, MenuCategory, MenuItem, OptionGroup } from '../types';

// ─── Public Types ───────────────────────────────────────────────────────────

export interface SharedGroup {
  key: string;           // groupCode or id
  groupCode: string;     // display code
  nameEN: string;
  nameAR: string;
  min: number;
  max: number;
  options: { nameEN: string; nameAR: string; extraPrice: number }[];
  linkedItemCount: number;
  linkedItemNames: string[];
}

export interface OrchestratedItem {
  catNameEN: string;
  catNameAR: string;
  nameEN: string;
  nameAR: string;
  descEN: string;
  descAR: string;
  price: number;
  image?: string;
  /** Unique groups — render inline as before */
  uniqueGroups: {
    nameEN: string;
    nameAR: string;
    min: number;
    max: number;
    options: { nameEN: string; nameAR: string; extraPrice: number }[];
  }[];
  /** Shared group codes — render as references only */
  sharedGroupCodes: string[];
  /** All shared group names (for Template 2 inline display) */
  sharedGroupNames: { en: string; ar: string }[];
}

export interface OrchestratedCategory {
  nameEN: string;
  nameAR: string;
  items: OrchestratedItem[];
}

export interface OrchestratedExport {
  brandName: string;
  profileName: string;
  categories: OrchestratedCategory[];
  sharedGroups: SharedGroup[];
  hasSharedGroups: boolean;
}

// ─── Analysis Engine ────────────────────────────────────────────────────────

interface GroupUsageEntry {
  grp: OptionGroup;
  itemCount: number;
  itemNames: string[];
}

function buildGroupUsageMap(categories: MenuCategory[]): Map<string, GroupUsageEntry> {
  const map = new Map<string, GroupUsageEntry>();

  for (const cat of categories) {
    if (cat.hidden) continue;
    for (const item of cat.items) {
      if (item.hidden) continue;
      for (const grp of item.optionGroups || []) {
        if (grp.hidden) continue;
        const key = grp.groupCode || grp.linkedLibraryId || grp.id;
        const existing = map.get(key);
        if (existing) {
          existing.itemCount++;
          if (!existing.itemNames.includes(item.nameEN)) {
            existing.itemNames.push(item.nameEN);
          }
        } else {
          map.set(key, { grp: { ...grp, options: grp.options.map(o => ({ ...o })) }, itemCount: 1, itemNames: [item.nameEN] });
        }
      }
    }
  }

  return map;
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function orchestrateExport(profile: MenuProfile): OrchestratedExport {
  const visibleCats = profile.categories.filter(c => !c.hidden && c.id !== '__unassigned__');
  const usageMap = buildGroupUsageMap(visibleCats);

  // Classify groups
  const sharedKeys = new Set<string>();
  const sharedGroupsList: SharedGroup[] = [];

  for (const [key, entry] of usageMap) {
    if (entry.itemCount > 1) {
      sharedKeys.add(key);
      sharedGroupsList.push({
        key,
        groupCode: entry.grp.groupCode || key,
        nameEN: entry.grp.nameEN,
        nameAR: entry.grp.nameAR || '',
        min: entry.grp.minSelection ?? 0,
        max: entry.grp.maxSelection ?? 0,
        options: entry.grp.options.filter(o => !o.hidden).map(o => ({
          nameEN: o.nameEN,
          nameAR: o.nameAR || '',
          extraPrice: o.extraPrice || 0,
        })),
        linkedItemCount: entry.itemCount,
        linkedItemNames: entry.itemNames,
      });
    }
  }

  sharedGroupsList.sort((a, b) => a.groupCode.localeCompare(b.groupCode));

  // Build orchestrated categories
  const categories: OrchestratedCategory[] = visibleCats.map(cat => ({
    nameEN: cat.nameEN,
    nameAR: cat.nameAR || '',
    items: cat.items.filter(i => !i.hidden).map(item => {
      const uniqueGroups: OrchestratedItem['uniqueGroups'] = [];
      const sharedGroupCodes: string[] = [];
      const sharedGroupNames: { en: string; ar: string }[] = [];

      for (const grp of item.optionGroups || []) {
        if (grp.hidden) continue;
        const key = grp.groupCode || grp.linkedLibraryId || grp.id;
        if (sharedKeys.has(key)) {
          const code = grp.groupCode || key;
          if (!sharedGroupCodes.includes(code)) {
            sharedGroupCodes.push(code);
            sharedGroupNames.push({ en: grp.nameEN, ar: grp.nameAR || '' });
          }
        } else {
          uniqueGroups.push({
            nameEN: grp.nameEN,
            nameAR: grp.nameAR || '',
            min: grp.minSelection ?? 0,
            max: grp.maxSelection ?? 0,
            options: grp.options.filter(o => !o.hidden).map(o => ({
              nameEN: o.nameEN,
              nameAR: o.nameAR || '',
              extraPrice: o.extraPrice || 0,
            })),
          });
        }
      }

      return {
        catNameEN: cat.nameEN,
        catNameAR: cat.nameAR || '',
        nameEN: item.nameEN,
        nameAR: item.nameAR || '',
        descEN: item.descriptionEN || '',
        descAR: item.descriptionAR || '',
        price: item.basePrice,
        image: item.image,
        uniqueGroups,
        sharedGroupCodes,
        sharedGroupNames,
      };
    }),
  }));

  return {
    brandName: profile.brandName,
    profileName: profile.name,
    categories,
    sharedGroups: sharedGroupsList,
    hasSharedGroups: sharedGroupsList.length > 0,
  };
}
